// NPM Imports
import React, { useContext, useEffect, useState } from 'react'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
// Component Imports
import PayrollImpTypeModal, {
   usePayrollImpTypeModal,
} from '../misc/PayrollImpTypeModal'
import Description from '../../ui-helper-elements/Description'
// JS Imports
import {
   //@ts-ignore
   formatFloatAsPercent,
   //@ts-ignore
   formatFloatAsCurrencySymbol,
} from '../../../common/helpers.mjs'
// Asset Imports
import Button from 'react-bootstrap/Button'
import EINIntegrationsTable from './EINIntegrationsTable'
import { SpacerThick } from '../../ui-helper-elements'

const MultiEINTableRow = ({ index }: { index: number }) => {
   const state = useQuoteContext()
   const { getString } = state

   const permissions = state.featurePermissions['PayrollImpTypeSelect']
   const permissionsFoundations =
      state.featurePermissions['PayrollImpFoundationsType']
   const permissionsNone = state.featurePermissions['PayrollImpNoneType']
   const permissionsShowMRROnDifferenceQuotes =
      state.featurePermissions['ShowMRROnDifferenceQuotes']

   const maxLength = 40
   // Displayed EIN Name, EE Count, State Count
   const [einName, setEinName] = useState('')
   const [einEECount, setEinEECount] = useState(0)
   const [einStateCount, setEinStateCount] = useState(1)
   const [localEinStateCount, setLocalEinStateCount] = useState(1)
   const [stateCountInputValue, setStateCountInputValue] = useState('')
   const [einImpType, setEinImpType] = useState('full')
   const [einIntegrations, setEinIntegrations] = useState([])

   // Import the modal hook to handle foundations selection
   const { handleSelfSetupType } = usePayrollImpTypeModal()

   // Checks if implementation products are not in the pricebook
   const hideImpTotal =
      state.product_list['payrollImp']?.NotInPricebook ||
      state.product_list['payrollImpFoundations']?.NotInPricebook ||
      false

   // Get Permissions for EIN Integrations
   const integrationPermissions = state.featurePermissions['EINIntegrations']

   const [isEINStateCountValid, setIsEINStateCountValid] = useState(false)

   // Updates the UI when changes made
   useEffect(() => {
      setEinEECount(state.productPayrollEECounts[index] || 0)
      setEinName(state.productPayrollEINNames[index] || '')
      // Set state count with default value of 1
      const stateCount = state.productPayrollStateCounts[index] || 1
      setEinStateCount(stateCount)
      setLocalEinStateCount(stateCount)
      // Set input value - empty string for 1 to show placeholder, otherwise the actual value
      setStateCountInputValue(stateCount === 1 ? '' : stateCount.toString())
      setEinIntegrations(state.productEINIntegrations[index] || [])
      setEinImpType(state.productPayrollEINImpTypes[index] || 'full')
   }, [
      state.productPayrollEECounts,
      state.productPayrollEINNames,
      state.productPayrollStateCounts,
      state.productEINIntegrations,
      state.productPayrollEINImpTypes,
      index,
   ])

   // Handle implementation type change with modal for foundations
   const handleImpTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value

      // Check if selecting Foundations
      if (value === 'foundations') {
         // Check if any EIN already has foundations setup
         if (
            state.productPayrollEINImpTypes &&
            state.productPayrollEINImpTypes.includes('foundations')
         ) {
            // If foundations is already selected somewhere, just apply it directly
            setEinImpType(value)
            state.onProductPayrollImpTypeChange(value, index)
         } else {
            // Show modal to confirm eligibility
            handleSelfSetupType({
               target: { value: 'foundations' },
               currentEINIndex: index,
            })
         }
      } else {
         // For non-foundations options, just update directly
         setEinImpType(value)
         state.onProductPayrollImpTypeChange(value, index)
      }
   }

   return (
      <>
         <PayrollImpTypeModal />
         <tr className={einIntegrations.length > 0 ? 'hide-bottom-border' : ''}>
            {state.productPayrollEINCount > 0 && (
               <>
                  <td>EIN {index + 1}</td>
                  <td>
                     <div className='fast-tooltip-container'>
                        <input
                           className='form-control input-field-2'
                           type='text'
                           value={einName || ''}
                           maxLength={maxLength}
                           onChange={(e) =>
                              state.onProductPayrollEINNameChange(
                                 e.target.value,
                                 index,
                              )
                           }
                           disabled={
                              state.differenceQuote &&
                              !!state.accountSnowflake?.EINs__c?.[0]?.[index]
                           }
                        />
                        {einName && (
                           <span className='fast-tooltip'>{einName}</span>
                        )}
                     </div>
                  </td>
               </>
            )}
            <td>
               <input
                  className='form-control input-field-1'
                  type='number'
                  value={einEECount <= 0 ? '' : einEECount.toString()}
                  min={0}
                  placeholder={'0'}
                  onChange={(e) => {
                     const value = Number(e.target.value)
                     value > 0
                        ? state.onProductPayrollEINEECountChange(value, index)
                        : state.onProductPayrollEINEECountChange(0, index)
                  }}
                  onBlur={(e) => {
                     if (Number(e.target.value) > 0 && einStateCount == 0) {
                        state.onProductPayrollEINStateCountChange(1, index)
                     } else if (Number(e.target.value) == 0) {
                        state.onProductPayrollEINStateCountChange(0, index)
                     }
                  }}
               />
            </td>
            <td className='col-bhr-red'>
               {formatFloatAsPercent(
                  state.productPayrollMonthlyCosts[index]
                     .volumeDiscountedPEPMPercent,
               )}
            </td>
            {einEECount > 0 ? (
               <td className='col-bhr-red'>
                  {formatFloatAsPercent(
                     state.productPayrollMonthlyCosts[index]
                        .bundleDiscountedPEPMPercent,
                  )}
               </td>
            ) : (
               <td></td>
            )}

            <td>
               <input
                  className='form-control input-field-1'
                  type='number'
                  value={stateCountInputValue}
                  min={1}
                  placeholder={'1'}
                  onMouseDown={(e) => {
                     // Handle spinner button clicks
                     const target = e.target as HTMLInputElement
                     const rect = target.getBoundingClientRect()
                     const clickX = e.clientX - rect.left
                     const inputWidth = rect.width

                     // Check if click is on the right side where spinner buttons are (approximate)
                     if (clickX > inputWidth - 20) {
                        const clickY = e.clientY - rect.top
                        const inputHeight = rect.height

                        // Determine if it's up or down arrow based on Y position
                        if (clickY < inputHeight / 2) {
                           // Up arrow clicked
                           e.preventDefault()
                           const newValue = einStateCount + 1
                           state.onProductPayrollEINStateCountChange(
                              newValue,
                              index,
                           )
                           setLocalEinStateCount(newValue)
                           setStateCountInputValue(newValue.toString())
                        } else {
                           // Down arrow clicked - only decrement if > 1
                           e.preventDefault()
                           if (einStateCount > 1) {
                              const newValue = einStateCount - 1
                              state.onProductPayrollEINStateCountChange(
                                 newValue,
                                 index,
                              )
                              setLocalEinStateCount(newValue)
                              setStateCountInputValue(
                                 newValue === 1 ? '' : newValue.toString(),
                              )
                           }
                        }
                     }
                  }}
                  onKeyDown={(e) => {
                     // Handle keyboard arrows
                     if (e.key === 'ArrowUp') {
                        e.preventDefault()
                        const newValue = einStateCount + 1
                        state.onProductPayrollEINStateCountChange(
                           newValue,
                           index,
                        )
                        setLocalEinStateCount(newValue)
                        setStateCountInputValue(newValue.toString())
                     } else if (e.key === 'ArrowDown') {
                        e.preventDefault()
                        if (einStateCount > 1) {
                           const newValue = einStateCount - 1
                           state.onProductPayrollEINStateCountChange(
                              newValue,
                              index,
                           )
                           setLocalEinStateCount(newValue)
                           setStateCountInputValue(
                              newValue === 1 ? '' : newValue.toString(),
                           )
                        }
                     }
                  }}
                  onChange={(e) => {
                     const inputValue = e.target.value
                     // Only allow numeric input
                     if (inputValue === '' || /^[0-9]+$/.test(inputValue)) {
                        setStateCountInputValue(inputValue)
                        const numValue =
                           inputValue === '' ? 1 : Number(inputValue)
                        setLocalEinStateCount(numValue)
                     }
                  }}
                  onBlur={(e) => {
                     // Validate EIN State Count
                     const value = localEinStateCount
                     if (value < 1 || isNaN(value)) {
                        state.onProductPayrollEINStateCountChange(1, index)
                        setStateCountInputValue('') // Clear input to show placeholder
                     } else {
                        state.onProductPayrollEINStateCountChange(value, index)
                        // Keep the input value as is if it's not 1
                        if (value === 1) {
                           setStateCountInputValue('') // Clear input to show placeholder for 1
                        }
                     }
                  }}
               />
            </td>
            <td>
               {permissions?.includes(state.user_role) &&
                  state.user_view !== 'External' && (
                     <select
                        className='form-select-bhr2'
                        value={einImpType || 'full'}
                        disabled={einEECount === 0}
                        onChange={handleImpTypeChange}
                     >
                        <option value='full'>Full</option>
                        {einEECount < 51 &&
                           state.productPayrollStateCounts[index] <= 10 &&
                           !state.product_list['payrollImpFoundations']
                              ?.NotInPricebook &&
                           permissionsFoundations?.includes(
                              state.user_role,
                           ) && (
                              <option value='foundations'>Foundations</option>
                           )}
                        {permissionsNone?.includes(state.user_role) && (
                           <option value='none'>None</option>
                        )}
                     </select>
                  )}
            </td>
            {state.user_view !== 'External' &&
               (hideImpTotal ? (
                  <td></td>
               ) : (
                  <td>
                     {formatFloatAsCurrencySymbol(
                        state.payrollImplementationCosts[index].totalPrice,
                        state.currencyObject,
                     )}
                  </td>
               ))}

            {/* State charges column with Description */}
            {state.differenceQuote ? (
               <td className='hoverable'>
                  <Description
                     title='State Charges Details'
                     popoverClassName='popover-wide'
                     placement='left'
                     className=''
                     content={
                        <div style={{ width: '500px', padding: '0.5rem' }}>
                           <div className='row mb-2'>
                              <div
                                 className='col-4'
                                 style={{ textAlign: 'left' }}
                              >
                                 <strong></strong>
                              </div>
                              <div
                                 className='col-2'
                                 style={{ textAlign: 'right' }}
                              >
                                 <strong>{getString('global.Final')}</strong>
                              </div>
                              <div
                                 className='col-3'
                                 style={{ textAlign: 'right' }}
                              >
                                 <strong>{getString('global.Previous')}</strong>
                              </div>
                              <div
                                 className='col-3'
                                 style={{ textAlign: 'right' }}
                              >
                                 <strong>
                                    {getString('global.Difference')}
                                 </strong>
                              </div>
                           </div>
                           <div className='row mb-2'>
                              <div
                                 className='col-4'
                                 style={{
                                    textAlign: 'left',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {getString(
                                    'product.MultiEINTableRow.Hover.Body.StateCharges',
                                 )}
                              </div>
                              <div
                                 className='col-2'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {formatFloatAsCurrencySymbol(
                                    typeof state
                                       .productPayrollMonthlyStateCosts[
                                       index
                                    ] === 'object'
                                       ? state.productPayrollMonthlyStateCosts[
                                            index
                                         ].totalPrice
                                       : state.productPayrollMonthlyStateCosts[
                                            index
                                         ],
                                    state.currencyObjectUSD,
                                 )}
                              </div>
                              <div
                                 className='col-3'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {formatFloatAsCurrencySymbol(
                                    typeof state
                                       .productPayrollMonthlyStateCosts[
                                       index
                                    ] === 'object'
                                       ? state.productPayrollMonthlyStateCosts[
                                            index
                                         ].previousTotalPrice || 0
                                       : state.accountSnowflake?.EINs__c?.[5]?.[
                                            index
                                         ] || 0,
                                    state.currencyObjectUSD,
                                 )}
                              </div>
                              <div
                                 className='col-3'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {formatFloatAsCurrencySymbol(
                                    typeof state
                                       .productPayrollMonthlyStateCosts[
                                       index
                                    ] === 'object'
                                       ? state.productPayrollMonthlyStateCosts[
                                            index
                                         ].totalPriceDifference || 0
                                       : (typeof state
                                            .productPayrollMonthlyStateCosts[
                                            index
                                         ] === 'number'
                                            ? state
                                                 .productPayrollMonthlyStateCosts[
                                                 index
                                              ]
                                            : 0) -
                                            (state.accountSnowflake
                                               ?.EINs__c?.[5]?.[index] || 0),
                                    state.currencyObjectUSD,
                                 )}
                              </div>
                           </div>
                           <div className='row mb-2'>
                              <div
                                 className='col-4'
                                 style={{
                                    textAlign: 'left',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {getString(
                                    'product.MultiEINTableRow.Hover.Body.StateCount',
                                 )}
                              </div>
                              <div
                                 className='col-2'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {einStateCount}
                              </div>
                              <div
                                 className='col-3'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {state.accountSnowflake?.EINs__c?.[6]?.[
                                    index
                                 ] || 0}
                              </div>
                              <div
                                 className='col-3'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {einStateCount -
                                    (state.accountSnowflake?.EINs__c?.[6]?.[
                                       index
                                    ] || 0)}
                              </div>
                           </div>

                           {/* Add MRR row for state charges */}
                           {permissionsShowMRROnDifferenceQuotes?.includes(
                              state.user_role,
                           ) && (
                              <>
                                 <SpacerThick></SpacerThick>
                                 {/* MRR Row Title */}
                                 <div className='row mb-2'>
                                    <div
                                       className='col-4'
                                       style={{ textAlign: 'left' }}
                                    >
                                       <strong></strong>
                                    </div>
                                    <div
                                       className='col-2'
                                       style={{ textAlign: 'right' }}
                                    >
                                       <strong>
                                          {getString('global.Final')}
                                       </strong>
                                    </div>
                                    <div
                                       className='col-3'
                                       style={{ textAlign: 'right' }}
                                    >
                                       <strong>
                                          {getString('global.Previous')}
                                       </strong>
                                    </div>
                                    <div
                                       className='col-3'
                                       style={{ textAlign: 'right' }}
                                    >
                                       <strong>
                                          {getString('global.DifferenceUpsell')}
                                       </strong>
                                    </div>
                                 </div>
                                 {/* MRR row */}
                                 <div className='row mb-2'>
                                    <div
                                       className='col-4'
                                       style={{
                                          textAlign: 'left',
                                          whiteSpace: 'nowrap',
                                       }}
                                    >
                                       {getString(
                                          'product.MultiEINTableRow.Hover.Body.MRR',
                                       )}
                                    </div>
                                    <div
                                       className='col-2'
                                       style={{
                                          textAlign: 'right',
                                          whiteSpace: 'nowrap',
                                       }}
                                    >
                                       {formatFloatAsCurrencySymbol(
                                          typeof state
                                             .productPayrollMonthlyStateCosts[
                                             index
                                          ] === 'object'
                                             ? state
                                                  .productPayrollMonthlyStateCosts[
                                                  index
                                               ].MRRTotal || 0
                                             : 0,
                                          state.currencyObjectUSD,
                                       )}
                                    </div>
                                    <div
                                       className='col-3'
                                       style={{
                                          textAlign: 'right',
                                          whiteSpace: 'nowrap',
                                       }}
                                    >
                                       {formatFloatAsCurrencySymbol(
                                          typeof state
                                             .productPayrollMonthlyStateCosts[
                                             index
                                          ] === 'object'
                                             ? state
                                                  .productPayrollMonthlyStateCosts[
                                                  index
                                               ].previousMRRTotal || 0
                                             : 0,
                                          state.currencyObjectUSD,
                                       )}
                                    </div>
                                    <div
                                       className='col-3'
                                       style={{
                                          textAlign: 'right',
                                          whiteSpace: 'nowrap',
                                       }}
                                    >
                                       {formatFloatAsCurrencySymbol(
                                          typeof state
                                             .productPayrollMonthlyStateCosts[
                                             index
                                          ] === 'object'
                                             ? (state
                                                  .productPayrollMonthlyStateCosts[
                                                  index
                                               ].MRRTotal || 0) -
                                                  (state
                                                     .productPayrollMonthlyStateCosts[
                                                     index
                                                  ].previousMRRTotal || 0)
                                             : 0,
                                          state.currencyObjectUSD,
                                       )}
                                    </div>
                                 </div>
                              </>
                           )}
                        </div>
                     }
                  >
                     {formatFloatAsCurrencySymbol(
                        typeof state.productPayrollMonthlyStateCosts[index] ===
                           'object'
                           ? state.productPayrollMonthlyStateCosts[index]
                                .totalPrice
                           : state.productPayrollMonthlyStateCosts[index],
                        state.currencyObjectUSD,
                     )}
                  </Description>
               </td>
            ) : (
               <td>
                  {formatFloatAsCurrencySymbol(
                     typeof state.productPayrollMonthlyStateCosts[index] ===
                        'object'
                        ? state.productPayrollMonthlyStateCosts[index]
                             .totalPrice
                        : state.productPayrollMonthlyStateCosts[index],
                     state.currencyObjectUSD,
                  )}
               </td>
            )}

            {/* Monthly charges column with Description */}
            {state.differenceQuote ? (
               <td className='hoverable'>
                  <Description
                     title='Upgrade Details'
                     popoverClassName='popover-wide'
                     placement='left'
                     className=''
                     content={
                        <div style={{ width: '500px', padding: '0.5rem' }}>
                           <div className='row mb-2'>
                              <div
                                 className='col-4'
                                 style={{ textAlign: 'left' }}
                              >
                                 <strong></strong>
                              </div>
                              <div
                                 className='col-2'
                                 style={{ textAlign: 'right' }}
                              >
                                 <strong>{getString('global.Final')}</strong>
                              </div>
                              <div
                                 className='col-3'
                                 style={{ textAlign: 'right' }}
                              >
                                 <strong>{getString('global.Previous')}</strong>
                              </div>
                              <div
                                 className='col-3'
                                 style={{ textAlign: 'right' }}
                              >
                                 <strong>
                                    {getString('global.Difference')}
                                 </strong>
                              </div>
                           </div>
                           <div className='row mb-2'>
                              <div
                                 className='col-4'
                                 style={{
                                    textAlign: 'left',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {getString(
                                    'product.MultiEINTableRow.Hover.Body.InvoiceAmount',
                                 )}
                              </div>
                              <div
                                 className='col-2'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {formatFloatAsCurrencySymbol(
                                    state.productPayrollMonthlyCosts[index]
                                       .totalPrice,
                                    state.currencyObjectUSD,
                                 )}
                              </div>
                              <div
                                 className='col-3'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {formatFloatAsCurrencySymbol(
                                    state.productPayrollMonthlyCosts[index]
                                       .previousTotalPrice,
                                    state.currencyObjectUSD,
                                 )}
                              </div>
                              <div
                                 className='col-3'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {formatFloatAsCurrencySymbol(
                                    state.productPayrollMonthlyCosts[index]
                                       .totalPriceDifference,
                                    state.currencyObjectUSD,
                                 )}
                              </div>
                           </div>
                           <div className='row mb-2'>
                              <div
                                 className='col-4'
                                 style={{
                                    textAlign: 'left',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {getString(
                                    'product.MultiEINTableRow.Hover.Body.Employees',
                                 )}
                              </div>
                              <div
                                 className='col-2'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {einEECount}
                              </div>
                              <div
                                 className='col-3'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {state.accountSnowflake?.EINs__c?.[4]?.[index]}
                              </div>
                              <div
                                 className='col-3'
                                 style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 {einEECount -
                                    (state.accountSnowflake?.EINs__c?.[4]?.[
                                       index
                                    ] || 0)}
                              </div>
                           </div>
                           {permissionsShowMRROnDifferenceQuotes?.includes(
                              state.user_role,
                           ) && (
                              <>
                                 <SpacerThick></SpacerThick>
                                 {/* MRR Row Title */}
                                 <div className='row mb-2'>
                                    <div
                                       className='col-4'
                                       style={{ textAlign: 'left' }}
                                    >
                                       <strong></strong>
                                    </div>
                                    <div
                                       className='col-2'
                                       style={{ textAlign: 'right' }}
                                    >
                                       <strong>
                                          {getString('global.Final')}
                                       </strong>
                                    </div>
                                    <div
                                       className='col-3'
                                       style={{ textAlign: 'right' }}
                                    >
                                       <strong>
                                          {getString('global.Previous')}
                                       </strong>
                                    </div>
                                    <div
                                       className='col-3'
                                       style={{ textAlign: 'right' }}
                                    >
                                       <strong>
                                          {getString('global.DifferenceUpsell')}
                                       </strong>
                                    </div>
                                 </div>
                                 {/* MRR row */}
                                 <div className='row mb-2'>
                                    <div
                                       className='col-4'
                                       style={{
                                          textAlign: 'left',
                                          whiteSpace: 'nowrap',
                                       }}
                                    >
                                       {getString('global.CommissionableMRR')}
                                    </div>
                                    <div
                                       className='col-2'
                                       style={{
                                          textAlign: 'right',
                                          whiteSpace: 'nowrap',
                                       }}
                                    >
                                       {formatFloatAsCurrencySymbol(
                                          state.productPayrollMonthlyCosts[
                                             index
                                          ].MRRTotal +
                                             state.productPayrollMonthlyCosts[
                                                index
                                             ].previousMRRTotal,
                                          state.currencyObjectUSD,
                                       )}
                                    </div>
                                    <div
                                       className='col-3'
                                       style={{
                                          textAlign: 'right',
                                          whiteSpace: 'nowrap',
                                       }}
                                    >
                                       {formatFloatAsCurrencySymbol(
                                          state.productPayrollMonthlyCosts[
                                             index
                                          ].previousMRRTotal,
                                          state.currencyObjectUSD,
                                       )}
                                    </div>
                                    <div
                                       className='col-3'
                                       style={{
                                          textAlign: 'right',
                                          whiteSpace: 'nowrap',
                                       }}
                                    >
                                       {formatFloatAsCurrencySymbol(
                                          state.productPayrollMonthlyCosts[
                                             index
                                          ].MRRTotal,
                                          state.currencyObjectUSD,
                                       )}
                                    </div>
                                 </div>
                              </>
                           )}
                        </div>
                     }
                  >
                     {formatFloatAsCurrencySymbol(
                        state.productPayrollMonthlyCosts[index].totalPrice,
                        state.currencyObjectUSD,
                     )}
                  </Description>
               </td>
            ) : (
               <td>
                  {formatFloatAsCurrencySymbol(
                     state.productPayrollMonthlyCosts[index].totalPrice,
                     state.currencyObjectUSD,
                  )}
               </td>
            )}

            {/* Integration button column */}
            {(state.einIntegrationsCount > 0 ||
               integrationPermissions.includes(state.user_role)) && (
               <td>
                  <Button
                     size='sm'
                     onClick={() => state.onProductEINIntegrationAdd(index)}
                     disabled={
                        state.integrationTypes.length - 1 ===
                        einIntegrations.length
                     }
                  >
                     {getString(
                        'product.MultiEINTableRow.AddIntegrationButton',
                     )}
                  </Button>
               </td>
            )}
         </tr>
         {state.einIntegrationsCount > 0 ||
         integrationPermissions.includes(state.user_role) ? (
            einIntegrations.length > 0 && (
               <tr>
                  <td colSpan={10}>
                     <EINIntegrationsTable
                        einIndex={index}
                        einIntegrations={einIntegrations}
                     />
                  </td>
               </tr>
            )
         ) : (
            <></>
         )}
      </>
   )
}

export default MultiEINTableRow
